import { Component, computed, Input, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DatatableComponent } from '@siemens/ngx-datatable';
import _ from 'lodash';
import { SearchPurchaseOrdersResult } from '../../../../../../graphql';
import { BxUnverifiedAlertService } from '../../../../../core/services/bx-unverified-alert.service';
import { DialogService } from '../../../../../core/services/dialog.service';
import { JobsV2Service } from '../../../../../core/services/jobsV2.service';
import { FeatureService, PermissionService } from '../../../../../core/services/permission';
import { Job, PurchaseOrder, PurchaseOrderCreationSource, PurchaseOrderType } from '../../../../../data/models';
import { RepoJobsService } from '../../../../../data/repositories/repo-jobs.service';
import { SharedDataService } from '../../../../../data/services/shared-data.service';
import { ReportService } from '../../../../../reports/report.service';
import { MessagesService } from '../../../../messages/messages.service';
import { QuoteRequestsService } from '../../../../quoterequests/quoterequest.service';
import { SearchPurchaseOrdersVariables } from '../../../purchase-orders/domain/api/queries/SearchPurchaseOrders.generated';
import { PurchaseOrdersInvoiceService } from '../../../purchase-orders/purchase-orders-invoice.service';
import { PurchaseOrdersService } from '../../../purchase-orders/purchase-orders.service';

type PurchaseOrderGql = SearchPurchaseOrdersResult['results'][number] & { orderNumberString: string; syncTooltip: string };

@Component({
    selector: 'app-job-orders',
    templateUrl: './job-orders.component.html',
    styleUrls: [
        './job-orders.component.scss',
        '../../../../../../../src/app/shared/composable-styles/input.scss',
        '../../../../../../../src/app/shared/composable-styles/button.scss',
    ],
    standalone: false,
})
export class JobOrdersComponent implements OnInit {
    // @ts-ignore TS2564
    @Input() job: Job;
    type: PurchaseOrderType | undefined;

    isAllowedToEmail = false;
    isAllowedToPrint = false;
    disableModify = true;
    isEmailOrderListDisabled = true;

    purchaseOrders = signal<PurchaseOrderGql[]>([]);
    statusFilter = signal('All');
    searchText = signal<string>('');

    rows = computed(() => {
        if (this.statusFilter() === 'All') {
            return this.searchFilteredOrders();
        }

        const statusFilteredOrders = this.searchFilteredOrders().filter((order) => {
            return order.orderStatus === this.statusFilter();
        });

        return statusFilteredOrders;
    });

    searchFilteredOrders = computed(() => {
        const searchTerm = this.searchText();
        if (searchTerm.length > 0) {
            const searchFilteredOrders = this.purchaseOrders().filter((order) => {
                return (
                    order.orderNumberString.toLowerCase().includes(searchTerm) ||
                    order.description?.toLowerCase().includes(searchTerm) ||
                    order.supplierInvoiceRef?.toLowerCase().includes(searchTerm) ||
                    order.supplierDescription?.toLowerCase().includes(searchTerm)
                );
            });

            return searchFilteredOrders;
        }

        return this.purchaseOrders();
    });

    orderCounts = computed(() => {
        const counts = {
            all: 0,
            unsent: 0,
            sent: 0,
            partreceived: 0,
            received: 0,
            cancelled: 0,
            completed: 0,
        };

        this.searchFilteredOrders().forEach((p) => {
            if (p.orderType === this.type?.value) {
                counts.all++;
                switch (p.orderStatus) {
                    case 'Unsent':
                        counts.unsent++;
                        break;
                    case 'Sent':
                        counts.sent++;
                        break;
                    case 'PartReceived':
                        counts.partreceived++;
                        break;
                    case 'Received':
                        counts.received++;
                        break;
                    case 'Cancelled':
                        counts.cancelled++;
                        break;
                    case 'Completed':
                        counts.completed++;
                        break;
                }
            }
        });

        return counts;
    });

    @ViewChild('ordersTable') table!: DatatableComponent;

    constructor(
        private reportService: ReportService,
        private messagesService: MessagesService,
        private bxUnverifiedAlertService: BxUnverifiedAlertService,
        private purchaseOrdersService: PurchaseOrdersService,
        private purchaseOrdersInvoiceService: PurchaseOrdersInvoiceService,
        private sharedData: SharedDataService,
        private repoJobs: RepoJobsService,
        private jobsV2Service: JobsV2Service,
        public permissionService: PermissionService,
        public featureService: FeatureService,
        private quoteReqService: QuoteRequestsService,
        private dialogService: DialogService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        const type = this.route.snapshot.queryParams.type;
        if (!type || !this.repoJobs.GetPurchaseOrderType(type)) {
            const queryParams = {
                // @ts-ignore TS2532
                type: this.repoJobs.PurchaseOrderTypes[0].value,
            };

            this.router.navigate(['/jobs', 'list', this.job.ID_Job, 'costings', 'orders'], { queryParams });
        }
        if (type) {
            if (type === 'work' && this.featureService.isAvailable('compact-jobs')) {
                this.router.navigate(['/jobs', 'list', this.job.ID_Job, 'costings', 'orders'], { queryParams: { type: 'purchase' } });
            }

            this.type = this.repoJobs.GetPurchaseOrderType(type);
            this.setIsAllowedToPrint();
            this.setIsAllowedToEmail();
            this.#setDisableModify();
            this.#setIsEmailOrderListDisabled();

            this.load();
        }
    }

    #setIsEmailOrderListDisabled() {
        this.isEmailOrderListDisabled =
            (this.type?.value === 'purchase' &&
                (!this.permissionService.isAvailable('jobs.maintain-purchase-orders.print-email-purchase-order-list') ||
                    !this.featureService.isStateAvailable('jobs.maintain-purchase-orders.send-purchase-order'))) ||
            (this.type?.value === 'work' &&
                (!this.permissionService.isAvailable('jobs.maintain-work-orders.print-email-work-order-list') ||
                    !this.featureService.isStateAvailable('jobs.maintain-work-orders.send-work-order')));
    }

    #setDisableModify() {
        this.disableModify =
            (this.type?.value === 'purchase' &&
                !this.permissionService.isAvailable('jobs.maintain-purchase-orders.modify-purchase-order')) ||
            (this.type?.value === 'work' && !this.permissionService.isAvailable('jobs.maintain-work-orders.modify-work-order'));
    }

    handleSearchInput(event: Event) {
        const searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.searchText.set(searchTerm);
    }

    load() {
        const request: SearchPurchaseOrdersVariables['request'] = {
            orderType: this.type?.value,
            jobId: this.job.ID_Job,
            orderByDesc: true,
            rowOffset: 0,
            rowFetch: 1000,
        };

        this.jobsV2Service.searchPurchaseOrders(request).subscribe((result) => {
            const orders = result.data!.searchPurchaseOrders.results.map((po) => ({
                ...po,
                orderNumberString: `${po.orderNumber}`.padStart(4, '0'),
                syncTooltip: `Synced ${po.fromAccSyncImport ? 'from' : 'to'} your accounting software`,
            }));

            this.purchaseOrders.set(orders);
        });
    }

    async viewOrder(order: PurchaseOrderGql) {
        const breezeOrder = await this.#getBreezeOrder(order);
        if (!breezeOrder) return;
        await this.purchaseOrdersService.viewOrder(breezeOrder);
        this.load();
    }

    async addOrder() {
        try {
            await this.purchaseOrdersService.newOrder(this.job, {
                OrderType: this.type?.value,
                CreationSource: PurchaseOrderCreationSource.NEWORDER,
            });
        } finally {
            this.load();
        }
    }

    termsAndConditions() {
        if (!this.type?.value) return;
        this.purchaseOrdersService.viewTermsAndConditions(this.type?.value);
    }

    async deleteJobOrder(evt: Event, order: PurchaseOrderGql) {
        evt.stopPropagation();

        const params = {
            title: `Delete order ${order.orderNumberString}?`,
            text: 'You will not be able to recover this order',
        };

        try {
            await this.dialogService.show(params);
            const breezeOrder = await this.#getBreezeOrder(order);
            if (!breezeOrder) return;
            breezeOrder.IsDeleted = true;
            this.repoJobs.Get(order.iD_Reference);
            await this.repoJobs.Save(null, 'Order has been deleted');
            this.repoJobs.SyncJobItemsWithPurchaseOrders(breezeOrder.Job);
            this.quoteReqService.unLinkPurchaseOrderFromRFQ(breezeOrder);
            this.load();
        } catch {}
    }

    async printPurchaseOrder(order: PurchaseOrderGql) {
        const breezeOrder = await this.#getBreezeOrder(order);
        if (breezeOrder) this.purchaseOrdersService.printOrder(breezeOrder);
    }

    // TODO:: move to shared component to slim the product
    async emailPurchaseOrder(order: PurchaseOrderGql) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }
        const breezeOrder = await this.#getBreezeOrder(order);
        if (!breezeOrder) return;

        await this.purchaseOrdersService.emailOrder(breezeOrder);
        this.load();
    }

    printPurchaseOrdersList() {
        let params;

        if (this.type?.value === 'purchase') {
            params = {
                reportName: 'purchaseorderbyjoblist',
                reportTitle: 'Purchase Orders List Report',
                params: {
                    ID_Reference: this.job.ID_Job,
                    OrderType: this.type?.value,
                },
                fileName: this.job.Number ? this.job.Number + '-Orders' : '',
            };
        } else if (this.type?.value === 'work') {
            params = {
                reportName: 'purchaseorderbyjoblist',
                reportTitle: 'Work Orders List Report',
                params: {
                    ID_Reference: this.job.ID_Job,
                    OrderType: this.type?.value,
                },
                fileName: this.job.Number ? this.job.Number + '-WorkOrders' : '',
            };
        }
        // @ts-ignore TS2345
        this.reportService.generate(params);
    }

    // TODO:: move to shared component to slim the product
    emailPurchaseOrdersList() {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        let params;

        if (this.type?.value === 'purchase') {
            params = {
                reportName: 'purchaseorderbyjoblist',
                reportTitle: 'Purchase Orders List Report',
                params: {
                    ID_Reference: this.job.ID_Job,
                    OrderType: this.type?.value,
                },
                format: 'pdf',
                fileName: this.job.Number ? this.job.Number + '-Orders' : '',
            };
        } else if (this.type?.value === 'work') {
            params = {
                reportName: 'purchaseorderbyjoblist',
                reportTitle: 'Work Orders List Report',
                params: {
                    ID_Reference: this.job.ID_Job,
                    OrderType: this.type?.value,
                },
                format: 'pdf',
                fileName: this.job.Number ? this.job.Number + '-WorkOrders' : '',
            };
        }
        // @ts-ignore TS2532
        this.reportService.generate(params).then((response) => {
            this.messagesService.sendEmail({
                To: this.job.ClientEmail
                    ? [
                          {
                              email: this.job.ClientEmail,
                              name: this.job.ClientName,
                          },
                      ]
                    : [],
                Subject: this.job.FullDescription + ' - Orders',
                Body: this.job.ClientName ? 'Dear ' + _.escape(this.job.ClientName) + ',' : '',
                StorageAttachments: [response.data],
                references: [
                    {
                        Type: 'job',
                        Id: this.job.ID_Job,
                    },
                    {
                        Type: 'estimate',
                        Id: this.job.ID_Estimate,
                    },
                ],
            });
        });
    }

    async addOrderInvoice(order: PurchaseOrderGql) {
        const breezeOrder = await this.#getBreezeOrder(order);
        if (!breezeOrder) return;

        try {
            await this.purchaseOrdersInvoiceService.addOrderInvoice(breezeOrder);
        } catch {}
    }

    setIsAllowedToPrint() {
        switch (this.type?.value) {
            case 'purchase':
                this.isAllowedToPrint = this.permissionService.isAvailable('jobs.maintain-purchase-orders.send-purchase-order');
                break;
            case 'work':
                this.isAllowedToPrint = this.permissionService.isAvailable('jobs.maintain-work-orders.send-work-order');
                break;
            default:
                this.isAllowedToPrint = true;
        }
    }

    setIsAllowedToEmail() {
        switch (this.type?.value) {
            case 'purchase':
                this.isAllowedToEmail =
                    this.permissionService.isAvailable('jobs.maintain-purchase-orders.send-purchase-order') &&
                    this.featureService.isStateAvailable('jobs.maintain-purchase-orders.send-purchase-order');
                break;
            case 'work':
                this.isAllowedToEmail =
                    this.permissionService.isAvailable('jobs.maintain-work-orders.send-work-order') &&
                    this.featureService.isStateAvailable('jobs.maintain-work-orders.send-work-order');
                break;
            default:
                this.isAllowedToEmail = true;
        }
    }

    async #getBreezeOrder(order: PurchaseOrderGql) {
        const reply = await this.repoJobs.GetJobPurchaseOrder(order.iD_PurchaseOrder, order.iD_Reference);
        const breezeOrder = reply.results[0] as PurchaseOrder | undefined;
        return breezeOrder;
    }
}
